/**
 * ЖИВУЧЕСТЬ ВКЛАДКИ ПРИ ДЕПЛОЕ.
 *
 * ЧТО ЛЕЧИМ. Вкладка открыта на релизе A. Выкатили релиз B — файлы A с
 * диска исчезли. Вкладка живёт дальше, пока ей не понадобится ленивый
 * чанк: переход в другой раздел, открытие модалки. В этот момент
 * `import()` получает 404, падает с «Failed to fetch dynamically imported
 * module», и пользователь видит экран «Что-то пошло не так» — хотя сайт
 * полностью здоров, просто вкладка помнит адреса вчерашней сборки.
 *
 * ЧТО ДЕЛАЕМ. Один раз перезагружаем документ. Перезагрузка приносит
 * свежий HTML с актуальными именами чанков, и всё продолжает работать.
 *
 * ПОЧЕМУ РОВНО ОДИН РАЗ. Если новая сборка тоже не отдаёт нужный файл
 * (сломанный релиз, откат на полпути, мёртвый CDN), перезагрузка не
 * поможет — а без ограничителя страница начнёт моргать бесконечно. Это
 * хуже честного экрана ошибки: пользователь не может ни прочитать, ни
 * пожаловаться. Поэтому попытка одна, отметка о ней лежит в
 * `sessionStorage` (переживает перезагрузку, умирает вместе со вкладкой), и
 * следующая разрешена только через RELOAD_COOLDOWN_MS — этого хватает,
 * чтобы вкладка, живущая сутки, пережила несколько деплоев подряд, и
 * при этом не хватает на цикл.
 *
 * ПОЧЕМУ НЕ ЧИСТИМ ОТМЕТКУ ПОСЛЕ УДАЧНОЙ ЗАГРУЗКИ. Тогда второй провал
 * (тот же самый, если релиз действительно сломан) снова разрешил бы
 * перезагрузку — и цикл вернулся бы через заднюю дверь.
 *
 * Проверки: `e2e/tests/deploy-recovery.spec.ts`.
 */

/**
 * Восстановление ЗАПУЩЕНО. `location.reload()` не мгновенный: страница
 * живёт ещё сотни миллисекунд, и за это время роутер успевает попробовать
 * отрисовать маршрут, чей модуль не приехал, — оттуда прилетает уже другая
 * ошибка (`Cannot read properties of undefined (reading 'component')`,
 * поймано прогоном 31.08.2026). Она не про чанк, и без этого флага
 * ErrorBoundary показал бы экран краха, который мигнул бы и исчез вместе с
 * перезагрузкой. Флаг живёт до конца жизни документа — ровно столько,
 * сколько нужно.
 */
let reloadPending = false;

/** Идёт ли восстановительная перезагрузка прямо сейчас. */
export const isDeployReloadPending = (): boolean => reloadPending;

/**
 * Префикс лога о восстановительной перезагрузке. Вынесен в константу не
 * ради красоты: по нему спека `deploy-recovery.spec.ts` отличает НАШУ
 * перезагрузку от обычной навигации. Считать все загрузки документа
 * нельзя — часть ссылок в шапке обычные якоря, и переход по ним законно
 * перезагружает страницу (поймано прогоном 31.08.2026).
 */
export const DEPLOY_RELOAD_LOG = '[deploy] перезагружаемся из-за недоступного чанка';

/** Ключ отметки о попытке восстановления. */
const RELOAD_MARK_KEY = 'deployReloadAt';

/** Пауза между попытками: одна перезагрузка на вкладку в 10 минут. */
const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

/**
 * Подписи провалившейся загрузки модуля. Собраны по браузерам и сборщикам:
 * первые две — Vite/Chrome и Firefox, третья — Safari, остальные —
 * наследие webpack-чанков, которые ещё лежат в корне dist.
 */
const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|chunkloaderror|loading chunk \S+ failed/i;

/** Ошибка про недоехавший чанк, а не про наш код. */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }
  const text =
    typeof error === 'string'
      ? error
      : `${(error as Error).name ?? ''} ${(error as Error).message ?? ''}`;
  return CHUNK_ERROR_RE.test(text);
};

const now = (): number => Date.now();

/** Когда была прошлая попытка. Недоступное хранилище = «не было». */
const lastReloadAt = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARK_KEY)) || 0;
  } catch {
    return 0;
  }
};

const markReload = (): void => {
  try {
    window.sessionStorage.setItem(RELOAD_MARK_KEY, String(now()));
  } catch {
    // Приватный режим или заблокированное хранилище: отметку поставить
    // негде. Тогда лучше НЕ перезагружаться вовсе — см. reloadOnceAfterDeploy
  }
};

const canMarkReload = (): boolean => {
  try {
    const probe = `${RELOAD_MARK_KEY}:probe`;
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

/**
 * Однократная перезагрузка после деплоя.
 * @returns true, если перезагрузка запущена.
 */
export const reloadOnceAfterDeploy = (reason: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Без работающего sessionStorage ограничитель не на чем держать —
  // а неограниченная перезагрузка это и есть то самое моргание
  if (!canMarkReload()) {
    console.log('[deploy] восстановление пропущено: нет sessionStorage');
    return false;
  }

  const since = now() - lastReloadAt();
  if (since < RELOAD_COOLDOWN_MS) {
    console.log(
      `[deploy] восстановление уже пробовали ${Math.round(since / 1000)}с назад — не зацикливаемся`
    );
    return false;
  }

  console.log(`${DEPLOY_RELOAD_LOG}: ${reason}`);
  reloadPending = true;
  markReload();
  window.location.reload();
  return true;
};

let installed = false;

/**
 * Ставит перехватчики. Зовётся из клиентского entry — раньше любого
 * ленивого импорта.
 */
export const installDeployRecovery = (): void => {
  if (typeof window === 'undefined' || installed) {
    return;
  }
  installed = true;

  // Штатное событие Vite: не смог подтянуть предзагруженный модуль.
  // preventDefault() гасим ТОЛЬКО если перезагрузка действительно пошла:
  // иначе (ограничитель уже сработал) ошибку надо пропустить дальше, чтобы
  // ErrorBoundary показал честный экран, а не пустую висящую страницу.
  window.addEventListener('vite:preloadError', event => {
    if (reloadOnceAfterDeploy('vite:preloadError')) {
      event.preventDefault();
    }
  });

  // import() внутри промиса, до которого не дотянется ErrorBoundary
  window.addEventListener('unhandledrejection', event => {
    if (isChunkLoadError(event.reason)) {
      reloadOnceAfterDeploy('unhandledrejection');
    }
  });
};
